/* Common Imports */
import { Box, styled } from "@mui/system";
import React from "react";

/* Component Imports */
import { IconButton, MenuItem, Select, Stack, SwipeableDrawer, TextField, Typography } from "@mui/material";

import { CheckCircleOutlined, Close, RocketLaunch } from "@mui/icons-material";
import { ObjectId } from "bson";
import { setAiBoostFormOfflinePaymentThunk } from "../../redux-magic/thunks";
import BeegruButton from "../common-components/buttons/BeegruButton";

/* Styled Components */
const DrawerHeader = styled(Box)(({ theme }) => ({
	padding: "16px",
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "center",
	borderBottom: "1px solid #E0E0E0",
	backgroundColor: "#FFFFFF",
	position: "sticky",
	top: 0,
	zIndex: 1,
	paddingTop: "env(safe-area-inset-top)",
	"@media (min-width: 600px)": {
		padding: "16px 24px",
	},
}));

const DrawerContent = styled(Box)(({ theme }) => ({
	padding: "1rem",
	display: "flex",
	flexDirection: "column",
	gap: "0.25rem",
	backgroundColor: "#FFFFFF",
	overflowY: "auto",
	flex: 1,
	WebkitOverflowScrolling: "touch",
	paddingBottom: "120px",
	height: "100%",
	"@media (min-width: 600px)": {
		padding: "1rem 1.5rem",
		gap: "0.5rem",
		paddingBottom: "96px",
	},
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
	display: "flex",
	justifyContent: "space-between",
	padding: "16px",
	backgroundColor: "#FFFFFF",
	position: "fixed",
	bottom: 0,
	left: 0,
	right: 0,
	zIndex: 2,
	gap: "0.5rem",
	borderTop: "1px solid #E0E0E0",
	borderRadius: "1rem 1rem 0 0",
	paddingBottom: "max(16px, env(safe-area-inset-bottom))", // Account for iOS home indicator
	"@media (min-width: 600px)": {
		padding: "16px 24px",
		gap: "1rem",
		paddingBottom: "16px",
	},
}));

const AiBoostFormOfflineMobileModal = ({
	open,
	handleClose,
	dispatch,
	propertyId,
	source,
}: {
	open: boolean;
	handleClose: () => void;
	dispatch: any;
	propertyId: ObjectId | string;
	source: { url: string; component: string; form_type: string };
}) => {
	const [currentStep, setCurrentStep] = React.useState<number>(0);
	const [message, setMessage] = React.useState<string>("");
	const [propertyData, setPropertyData] = React.useState<any>({});
	const [phoneNumber, setPhoneNumber] = React.useState<string>("");
	const [countryCode, setCountryCode] = React.useState<string>("+91");
	const [enterpriseName, setEnterpriseName] = React.useState<string>("");
	const [propertyName, setPropertyName] = React.useState<string>("");
	const [loadingPropertyData, setLoadingPropertyData] = React.useState<boolean>(true);
	const [showCustomSuccess, setShowCustomSuccess] = React.useState(false);
	const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

	React.useEffect(() => {
		if (propertyId) {
			fetch(`${process.env.PRODUCTION_API_URL}properties/cards/${propertyId}`, {
				method: "POST",
				headers: {
					"Content-Type": "text/plain",
				},
			})
				.then((res) => res.json())
				.then((data) => {
					setCountryCode(data.property_data.created_by?.business_profile_id.phone.country_code || "");
					setPhoneNumber(data.property_data.created_by?.business_profile_id.phone.phone_number || "");
					setEnterpriseName(data.property_data.created_by?.business_profile_id.title || "");
					setPropertyName(data.property_data.title || "");
					setPropertyData(data.property_data);
					setLoadingPropertyData(false);
				});
		}
	}, [propertyId, dispatch]);

	const handleSubmit = async () => {
		setIsSubmitting(true);
		await dispatch(
			setAiBoostFormOfflinePaymentThunk({
				name: enterpriseName,
				phone: {
					phone_number: phoneNumber,
					country_code: countryCode,
				},
				notes: message,
				source: source,
				properties_to_boost: [
					{
						property_id: propertyData._id,
						property_name: propertyName,
						exists: true,
					},
				],
			}),
		);
		setCurrentStep(1);
		setShowCustomSuccess(true);
		setIsSubmitting(false);
	};

	return (
		<SwipeableDrawer
			anchor="bottom"
			open={open}
			onClose={handleClose}
			onOpen={() => {}}
			disableSwipeToOpen={false}
			ModalProps={{
				keepMounted: true,
			}}
			PaperProps={{
				sx: {
					height: "85vh",
					backgroundColor: "#FFFFFF",
					display: "flex",
					flexDirection: "column",
					borderTopLeftRadius: "1rem",
					borderTopRightRadius: "1rem",
					overflow: "hidden",
					"& .MuiDrawer-paper": {
						overflowY: "auto",
						WebkitOverflowScrolling: "touch",
					},
				},
			}}
		>
			<DrawerHeader>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
					<RocketLaunch sx={{ color: "#693A70" }} />
					<Typography variant="h6">AI Boost</Typography>
				</Box>
				<Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
					<IconButton onClick={handleClose}>
						<Close />
					</IconButton>
				</Box>
			</DrawerHeader>

			<DrawerContent>
				{currentStep === 0 ? (
					<Box>
						<Typography
							variant="h6"
							sx={{ mb: 1 }}
						>
							Fill the form
						</Typography>
						<Typography
							variant="body2"
							color="textSecondary"
							sx={{ mb: 3 }}
						>
							Please provide 1 your details so that we can discuss a plan that suits your need
						</Typography>
						<Stack spacing={3}>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
								<Typography
									variant="body2"
									sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#212121" }}
								>
									Enterprise Name*
								</Typography>
								<TextField
									fullWidth
									value={enterpriseName}
									onChange={(e) => setEnterpriseName(e.target.value)}
									placeholder="Enter your enterprise name"
									autoComplete="off"
								/>
							</Box>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 1, flex: 1 }}>
								<Typography
									variant="body2"
									sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#212121" }}
								>
									Phone Number*
								</Typography>
								<Box sx={{ display: "flex", gap: 1 }}>
									<Select
										value={countryCode}
										onChange={(e) => setCountryCode(e.target.value)}
										sx={{
											minWidth: "100px",
											"& .MuiSelect-select": {
												padding: "0.5rem",
											},
										}}
									>
										<MenuItem value="+91">+91 </MenuItem>
										<MenuItem value="+1">+1</MenuItem>
										<MenuItem value="+971">+971</MenuItem>
									</Select>
									<TextField
										fullWidth
										value={phoneNumber}
										onChange={(e) => {
											const inputValue = e.target.value;
											const numericValue = inputValue.replace(/\D/g, "");

											// Set max length based on country code
											const maxLength =
												countryCode === "+91" ? 10 : countryCode === "+1" ? 10 : countryCode === "+971" ? 9 : 15;

											if (numericValue.length <= maxLength) {
												setPhoneNumber(numericValue);
											}
										}}
										placeholder="Enter your phone number"
										autoComplete="off"
										error={phoneNumber.length > 0 && phoneNumber.length < 10}
										helperText={
											phoneNumber.length > 0 && phoneNumber.length < 10 ? "Phone number must be 10 digits" : ""
										}
										inputProps={{
											inputMode: "numeric",
											pattern: "[0-9]*",
										}}
									/>
								</Box>
							</Box>

							<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
								<Typography
									variant="body2"
									sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#212121" }}
								>
									Property Name
								</Typography>
								<TextField
									fullWidth
									value={propertyName}
									onChange={(e) => setPropertyName(e.target.value)}
									placeholder="Enter property name"
									autoComplete="off"
								/>
							</Box>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
								<Typography
									variant="body2"
									sx={{ fontSize: "0.875rem", fontWeight: 500, color: "#212121" }}
								>
									Notes(optional)
								</Typography>
								<TextField
									fullWidth
									value={message}
									onChange={(e) => setMessage(e.target.value)}
									placeholder="Enter your message"
									multiline
									rows={3}
									autoComplete="off"
								/>
							</Box>
						</Stack>
						<Typography
							variant="body2"
							color="textSecondary"
							sx={{ mt: 2 }}
						>
							Need help?{" "}
							<Typography
								component="span"
								variant="body2"
								sx={{
									color: "#693A70",
									cursor: "pointer",
									fontWeight: 500,
									textDecoration: "none",
									"&:hover": {
										textDecoration: "underline",
									},
								}}
								onClick={() => (window.location.href = "/contact-us")}
							>
								Contact us
							</Typography>
						</Typography>
					</Box>
				) : (
					<Box
						sx={{
							width: "100%",
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							p: { xs: 2, sm: 4 },
						}}
					>
						<Box
							sx={{
								display: "flex",
								flexDirection: "column",
								alignItems: "center",
								justifyContent: "center",
								width: "100%",
								textAlign: "center",
								mb: 4,
							}}
						>
							<CheckCircleOutlined
								sx={{
									fontSize: { xs: 48, sm: 64 },
									color: "#12744F",
									mb: 2,
								}}
							/>
							<Typography
								variant="h6"
								sx={{ mb: 1 }}
							>
								Details Submitted Successfully
							</Typography>
							<Typography
								variant="body1"
								color="textSecondary"
							>
								Our team member will contact you soon, to market your properties.
							</Typography>
						</Box>

						<Box
							sx={{
								width: "100%",
								backgroundColor: "#F0ECF1",
								borderRadius: "1rem",
								p: 2,
							}}
						>
							<Typography
								variant="subtitle1"
								sx={{ mb: 0.5, fontWeight: 600 }}
							>
								What&apos;s included ?
							</Typography>
							<Typography
								variant="body1"
								color="textSecondary"
								sx={{ mb: 3 }}
							>
								You will get all these features in action.
							</Typography>

							<Stack spacing={2}>
								<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
									<CheckCircleOutlined sx={{ color: "#1BA672" }} />
									<Typography>Live AI tasks</Typography>
								</Box>
								<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
									<CheckCircleOutlined sx={{ color: "#1BA672" }} />
									<Typography>24 x 7 support</Typography>
								</Box>
								<Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
									<CheckCircleOutlined sx={{ color: "#1BA672" }} />
									<Typography>Premium AI-targeting</Typography>
								</Box>
								<Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
									<CheckCircleOutlined sx={{ color: "#1BA672" }} />
									<Typography>Multi-platform marketing</Typography>
									<Box
										sx={{
											display: "flex",
											gap: 1,
											ml: { xs: 0, sm: "auto" },
											mt: { xs: 1, sm: 0 },
											width: { xs: "100%", sm: "auto" },
											justifyContent: { xs: "flex-start", sm: "flex-end" },
										}}
									>
										<Box sx={{ p: 0.75, bgcolor: "#FFFFFF", borderRadius: "4px" }}>
											<img
												src="/images/logo/facebook.png"
												alt="Facebook"
												style={{ height: 18, width: 76 }}
											/>
										</Box>
										<Box sx={{ p: 0.75, bgcolor: "#FFFFFF", borderRadius: "4px" }}>
											<img
												src="/images/logo/instagram.png"
												alt="Instagram"
												style={{ height: 18, width: 76 }}
											/>
										</Box>
										<Box sx={{ p: 0.75, bgcolor: "#FFFFFF", borderRadius: "4px" }}>
											<img
												src="/images/logo/google.png"
												alt="Google"
												style={{ height: 18, width: 76 }}
											/>
										</Box>
									</Box>
								</Box>
							</Stack>
						</Box>
					</Box>
				)}
			</DrawerContent>

			<ButtonContainer>
				{currentStep === 0 ? (
					<Stack
						direction="row"
						spacing={2}
						sx={{ width: "100%" }}
					>
						<BeegruButton
							variant="contained"
							flavor="boost"
							onClick={handleSubmit}
							disabled={isSubmitting}
							sx={{
								flex: 1,
								height: "48px",
								backgroundColor: "#693A70",
								color: "#FFFFFF",
								"&:hover": {
									backgroundColor: "#532b57",
								},
							}}
						>
							{isSubmitting ? "Submitting..." : "Submit Details"}
						</BeegruButton>
					</Stack>
				) : (
					<Stack
						direction="row"
						spacing={2}
						sx={{ width: "100%" }}
					>
						<BeegruButton
							variant="contained"
							flavor="boost"
							onClick={handleClose}
							sx={{
								flex: 1,
								height: "48px",
								backgroundColor: "#693A70",
								color: "#FFFFFF",
								"&:hover": {
									backgroundColor: "#532b57",
								},
							}}
						>
							Done
						</BeegruButton>
					</Stack>
				)}
			</ButtonContainer>
		</SwipeableDrawer>
	);
};

export default AiBoostFormOfflineMobileModal;
